import { useMemo } from 'react';

import { getCPM, getTotalPice } from '../../../../../services/display';
import { getDomain } from '../../../../../services/utils';
import Colors from '../../../../../themes/Colors';
import { resolveColor } from '../../../../../themes/theme';
import { getVenueGroupLabels } from '../../../../../services/venueCategories';

/**
 * Frequency weights by venue_type_id (OpenOOH taxonomy).
 * Determines how many times per day a unique individual is exposed to a display of that subtype.
 */
const FREQUENCY_WEIGHTS = {
  // Outdoor
  301: 2.0,
  302: 1.6,
  303: 2.4,
  // Indoor
  101: 5.0,
  105: 3.0,
  106: 3.0,
  10202: 3.0,
  205: 1.3,
  201: 1.2,
  202: 1.2,
  203: 1.2,
  204: 1.2,
  207: 1.2,
  804: 1.2,
  805: 1.2,
  806: 1.2,
  401: 4.5,
  402: 4.5,
  501: 4.5,
  601: 4.5,
  602: 4.5,
  701: 4.5,
  801: 4.5,
  1001: 4.5,
  // Movil
  103: 4.75,
  104: 2.5,
  102: 2.5
};

/**
 * Color palettes based on domain (DV360 uses green, Latinad uses blue)
 */
const CHART_COLORS = {
  investmentByType: (isDv360) => isDv360
    ? ["#0d652d", "#1a8040", "#34A853", "#5bc87a", "#81c995", "#b7dfbe"]
    : ["#0D65D8", "#B2D2FF", "#55A1F6", "#0243A9", "#00287A", "#00124C"],
  socioEconomic: (isDv360) => isDv360
    ? ["#0d652d", "#1a8040", "#34A853", "#5bc87a", "#81c995", "#d4efda"]
    : ["#0D65D8", "#278CF2", "#55A1F6", "#84B7FB", "#B2D2FF", "#DEEFFF"],
  gender: (isDv360) => isDv360
    ? ["#0d652d", "#1a8040", "#81c995", "#d4efda"]
    : ["#0243A9", "#278CF2", "#84B7FB", "#DEEFFF"],
  ageRange: (isDv360) => isDv360 ? "#34A853" : "#36A2EB",
};

/** Generates a palette of colors interpolated for subtypes and media owners. */
const generateThemeShades = (count, isDv360) => {
  const baseHue = isDv360 ? 136 : 217;
  const saturation = isDv360 ? 53 : 70;
  const colors = [];
  const startLightness = 85;
  const endLightness = 30;
  const step = count > 1 ? (startLightness - endLightness) / (count - 1) : 0;
  for (let i = 0; i < count; i++) {
    colors.push(
      `hsl(${baseHue}, ${saturation}%, ${startLightness - i * step}%)`
    );
  }
  return colors;
};

/**
 * Calculates weighted frequency, unique hits, and reach percentage
 * based on the distribution of investment among venue subtypes.
 */
const calculateAudienceMetrics = (campaign, totalImpacts, totalAudience) => {
  if (!campaign || !campaign.contents_displays || !totalImpacts) {
    return { weightedFreq: 0, uniqueImpacts: 0, reachPercentage: 0 };
  }

  const totalInvestment = parseFloat(campaign.total_price_with_profit);
  if (!totalInvestment || totalInvestment === 0) {
    return { weightedFreq: 0, uniqueImpacts: 0, reachPercentage: 0 };
  }

  const nativeContentsDisplays = Array.isArray(campaign.contents_displays)
    ? campaign.contents_displays
    : campaign.contents_displays.asMutable({ deep: true });

  const investmentByFreq = {};

  nativeContentsDisplays.forEach(display => {
    const subtypeId = display.display_open_ooh_venue_type_id;
    const freqValue = FREQUENCY_WEIGHTS[subtypeId] || 1.0;
    const amount = parseFloat(display.paid_amount_with_profit) || 0;

    if (!investmentByFreq[freqValue]) {
      investmentByFreq[freqValue] = 0;
    }
    investmentByFreq[freqValue] += amount;
  });

  let weightedFreq = 0;
  Object.keys(investmentByFreq).forEach(freq => {
    const groupInvestment = investmentByFreq[freq];
    const share = groupInvestment / totalInvestment;
    weightedFreq += parseFloat(freq) * share;
  });

  const uniqueImpacts = weightedFreq > 0 ? totalImpacts / weightedFreq : 0;
  const reachPercentage = totalAudience > 0 ? (totalImpacts / totalAudience) * 100 : 0;

  return { weightedFreq, uniqueImpacts, reachPercentage };
};

/**
 * Processes contents_displays to build investment datasets by type,
 * subtype, and media owner for InvestmentCharts graphs.
 */
const processInvestmentData = (campaign, cart, t) => {
  if (!campaign || !campaign.contents_displays || !campaign.contents_displays.length) {
    return {
      investmentByType: null,
      investmentBySubtype: null,
      investmentByMediaOwner: null
    };
  }

  const isDv360 = getDomain(window.location.hostname) === 'dv360';

  const nativeContentsDisplays = Array.isArray(campaign.contents_displays)
    ? campaign.contents_displays
    : campaign.contents_displays.asMutable({ deep: true });

  const mediaOwnerMap =
    cart && cart.length > 0
      ? cart.reduce((acc, display) => {
          if (display.company && display.company.id && display.company.name) {
            acc[display.company.id] = display.company.name;
          }
          return acc;
        }, {})
      : {};

  const aggregated = nativeContentsDisplays.reduce(
    (acc, item) => {
      const amount = parseFloat(item.paid_amount_with_profit);
      if (isNaN(amount)) return acc;

      // Type by OpenOOH root, subtype by its level 2 node.
      const { type, subtype } = getVenueGroupLabels(item, t);
      acc.byType[type] = (acc.byType[type] || 0) + amount;
      if (!acc.bySubtype[type]) acc.bySubtype[type] = {};
      acc.bySubtype[type][subtype] = (acc.bySubtype[type][subtype] || 0) + amount;

      const ownerId = item.display_company_id;
      let ownerName = mediaOwnerMap[ownerId];
      if (!ownerName) {
        ownerName = item.display_company_name || item.company_name || `ID ${ownerId}`;
      }

      acc.byOwner[ownerName] = (acc.byOwner[ownerName] || 0) + amount;

      return acc;
    },
    { byType: {}, bySubtype: {}, byOwner: {} }
  );

  // investment by screen type
  const investmentByType = {
    labels: Object.keys(aggregated.byType),
    datasets: [
      {
        data: Object.values(aggregated.byType),
        backgroundColor: CHART_COLORS.investmentByType(isDv360),
        borderColor: resolveColor(Colors.white),
        borderWidth: 2
      }
    ]
  };

  // investment by screen subtype
  const subtypeTypeLabels = Object.keys(aggregated.bySubtype);
  const allSubtypes = new Set(
    subtypeTypeLabels.flatMap(type => Object.keys(aggregated.bySubtype[type]))
  );
  const colorPaletteSubtype = generateThemeShades(allSubtypes.size, isDv360);
  const investmentBySubtype = {
    labels: subtypeTypeLabels,
    datasets: Array.from(allSubtypes).map((subtype, index) => ({
      label: subtype,
      data: subtypeTypeLabels.map(
        type => (aggregated.bySubtype[type] && aggregated.bySubtype[type][subtype]) || 0
      ),
      backgroundColor: colorPaletteSubtype[index]
    }))
  };

  // investment per media owner
  const ownerLabels = Object.keys(aggregated.byOwner);
  const colorPaletteOwner = generateThemeShades(ownerLabels.length, isDv360);
  const investmentByMediaOwner = {
    labels: ownerLabels,
    datasets: [
      {
        data: Object.values(aggregated.byOwner),
        backgroundColor: colorPaletteOwner,
        borderColor: resolveColor(Colors.white),
        borderWidth: 2
      }
    ]
  };

  return { investmentByType, investmentBySubtype, investmentByMediaOwner };
};

/**
 * Processes screen audience attributes to build sociodemographic
 * datasets (SES, gender, age range) for AudienceCharts.
 */
const processAudienceData = (attributes, t) => {
  if (!attributes || !attributes.length) {
    return { socioEconomic: null, gender: null, ageRange: null };
  }
  const isDv360 = getDomain(window.location.hostname) === 'dv360';
  const total = attributes.length;
  const sumas = attributes.reduce((acc, current) => {
    Object.keys(current).forEach(key => {
      if (typeof current[key] === 'number') acc[key] = (acc[key] || 0) + current[key];
    });
    return acc;
  }, {});
  const promedios = {};
  Object.keys(sumas).forEach(key => (promedios[key] = sumas[key] / total));

  // logic for socioeconomic level
  const socioEconomic =
    promedios.sec_nc < 100
      ? {
          labels: [
            t('Level_A'),
            t('Level_B'),
            t('Level_C'),
            t('Level_D'),
            t('Level_E'),
            t('NOT_SPECIFIED')
          ],
          datasets: [
            {
              data: [
                promedios.sec_a,
                promedios.sec_b,
                promedios.sec_c,
                promedios.sec_d,
                promedios.sec_e,
                promedios.sec_nc
              ],
              backgroundColor: CHART_COLORS.socioEconomic(isDv360),
              borderColor: resolveColor(Colors.white),
              borderWidth: 2
            }
          ]
        }
      : null;

  // logic for the gender chart
  const gender =
    promedios.gender_nc < 100
      ? {
          labels: [t('Male'), t('Female'), t('Other'), t('NOT_SPECIFIED')],
          datasets: [
            {
              data: [
                promedios.gender_male,
                promedios.gender_female,
                promedios.gender_other,
                promedios.gender_nc
              ],
              backgroundColor: CHART_COLORS.gender(isDv360),
              borderColor: resolveColor(Colors.white),
              borderWidth: 2
            }
          ]
        }
      : null;

  // logic for age range
  const ageKeys = [
    'age_children',
    'age_teens',
    'age_young_adults',
    'age_midlife_adults',
    'age_mature_adults',
    'age_seniors'
  ];
  const totalAgeAverage = ageKeys.reduce((sum, key) => sum + (promedios[key] || 0), 0);
  const ageRange =
    totalAgeAverage > 0
      ? {
          labels: ['0-14', '15-19', '20-29', '30-44', '45-54', '+55'],
          datasets: [
            {
              label: t('Age Range'),
              data: ageKeys.map(key => promedios[key]),
              backgroundColor: CHART_COLORS.ageRange(isDv360)
            }
          ]
        }
      : null;

  return { socioEconomic, gender, ageRange };
};

/**
 * Hook que centraliza el cálculo de métricas de audiencia para el step de checkout.
 *
 * @param {object} params
 * @param {object}  params.effectiveCampaign     - Objeto campaña con contents_displays
 * @param {object}  params.audience              - Respuesta de getAudience (total_audience, total_impacts)
 * @param {object}  params.proposalAudience      - Respuesta de getProposalAudience (max_audience_day/hour)
 * @param {Array}   params.campaignAudienceAttributes - Atributos sociodemográficos por pantalla
 * @param {object}  params.pricesData            - Respuesta de calculatePrices (items, tax, currency)
 * @param {string}  params.programmatic          - "programmatic" | "traditional"
 * @param {number}  params.amountBudget          - Budget total (solo para campañas programáticas)
 * @param {Array}   params.cart                  - Pantallas en el carrito
 * @param {string}  params.date_from
 * @param {string}  params.date_to
 * @param {Function} params.t                    - Función de traducción i18n
 *
 * @returns {{ audienceData: object|null }}
 */
const useCheckoutAudienceData = ({
  effectiveCampaign,
  audience,
  proposalAudience,
  campaignAudienceAttributes,
  pricesData,
  programmatic,
  amountBudget,
  cart,
  date_from,
  date_to,
  t
}) => {
  const audienceData = useMemo(() => {
    if (!effectiveCampaign) return null;

    const safeAudience = audience || { total_audience: 0, total_impacts: 0 };
    const safeProposalAudience = proposalAudience || {};

    const dayMap = [
      t('Sunday'),
      t('Monday'),
      t('Tuesday'),
      t('Wednesday'),
      t('Thursday'),
      t('Friday'),
      t('Saturday')
    ];

    const totalExits =
      pricesData && pricesData.items
        ? pricesData.items.reduce(
            (sum, item) => sum + (item.calculations_info.promised_shows_with_discount || 0),
            0
          )
        : 0;

    const campaignPrice =
      programmatic === 'programmatic' ? amountBudget : getTotalPice(cart || [], date_from, date_to);
    const cpm = getCPM(campaignPrice, safeAudience.total_impacts);

    const { weightedFreq, uniqueImpacts, reachPercentage } = calculateAudienceMetrics(
      effectiveCampaign,
      safeAudience.total_impacts,
      safeAudience.total_audience
    );

    return {
      metrics: {
        reach: safeAudience.total_audience
          ? safeAudience.total_audience.toLocaleString('es-AR')
          : 0,
        potential_reach:
          reachPercentage.toLocaleString('es-AR', { maximumFractionDigits: 2 }) + '%',
        impacts: safeAudience.total_impacts
          ? safeAudience.total_impacts.toLocaleString('es-AR')
          : 0,
        total_exits: totalExits.toLocaleString('es-AR') || 0,
        average_cpm: cpm || 0,
        frequency_of_unique_individuals: weightedFreq.toLocaleString('es-AR', {
          maximumFractionDigits: 2
        }),
        total_unique_impacts: Math.round(uniqueImpacts).toLocaleString('es-AR')
      },
      performance: {
        day:
          safeProposalAudience.max_audience_day &&
          dayMap[safeProposalAudience.max_audience_day.day] != null
            ? dayMap[safeProposalAudience.max_audience_day.day]
            : '-',
        reach:
          safeProposalAudience.max_audience_day &&
          safeProposalAudience.max_audience_day.audience != null
            ? safeProposalAudience.max_audience_day.audience.toLocaleString('es-AR')
            : '-',
        hour:
          safeProposalAudience.max_audience_hour &&
          safeProposalAudience.max_audience_hour.hour != null
            ? `${String(safeProposalAudience.max_audience_hour.hour).padStart(2, '0')}:00 hs`
            : '-',
        hourlyReach:
          safeProposalAudience.max_audience_hour &&
          safeProposalAudience.max_audience_hour.audience != null
            ? safeProposalAudience.max_audience_hour.audience.toLocaleString('es-AR')
            : '-'
      },
      charts: {
        ...processAudienceData(campaignAudienceAttributes, t),
        ...processInvestmentData(effectiveCampaign, cart, t)
      }
    };
  }, [
    effectiveCampaign,
    cart,
    proposalAudience,
    campaignAudienceAttributes,
    t,
    audience,
    programmatic,
    amountBudget,
    date_from,
    date_to,
    pricesData
  ]);

  return { audienceData };
};

export default useCheckoutAudienceData;
